import { gql } from '@apollo/client';
import { Box, IconButton } from '@mui/material';
import { Tabs } from 'components/common/Tabs';
import { IconCustomSmiley } from 'components/icons/components/custom/IconCustomSmiley';
import { IconLinearCelo } from 'components/icons/components/linear/IconLinearCelo';
import { IconLinearLocation } from 'components/icons/components/linear/IconLinearLocation';
import { IconLinearNote } from 'components/icons/components/linear/IconLinearNote';
import { IconLinearShoppingCart } from 'components/icons/components/linear/IconLinearShoppingCart';
import { IconLinearSort } from 'components/icons/components/linear/IconLinearSort';
import { IconLinearTag2 } from 'components/icons/components/linear/IconLinearTag2';
import { IconLinearUser } from 'components/icons/components/linear/IconLinearUser';
import { IconOutlineLike } from 'components/icons/components/outline/IconOutlineLike';
import { NestedFiltersMenuView } from 'features/nestedFilters/views/menu';
import {
  RelationshipLabelMap,
  SentimentLabelMap,
} from 'features/socialListeningCommunity/constants';
import {
  CreatorProfileCommunitySortBy,
  CreatorProfileRelationshipStrength,
  Sentiment,
  SimpleDateRangeFilterInput,
  SortOrder,
  useGetCapturedProductsForSlCommunityTabLazyQuery,
  useGetLocationsForSlCommunityTabQuery,
  useGetPaginatedLabelsForSlCommunityTabLazyQuery,
} from 'graphql/generated';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlotRoutes } from 'Routes';
import { theme } from 'styles/theme';
import { SocialListeningCommunityCreators } from './SocialListeningCommunityCreators';
import { COMMUNITY_MODAL_TYPES } from './SocialListeningCommunityDialog';
import { SocialListeningCommunityPosts } from './SocialListeningCommunityPosts';

// eslint-disable-next-line
gql`
  query GetPaginatedLabelsForSLCommunityTab(
    $input: PaginatedOrganizationCreatorProfileLabelInput!
  ) {
    paginatedOrganizationCreatorProfileLabels(input: $input) {
      data {
        id
        label
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

// eslint-disable-next-line
gql`
  query GetLocationsForSLCommunityTab($brandId: String!) {
    getCreatorProfileLocations(brandId: $brandId)
  }
`;

// eslint-disable-next-line
gql`
  query GetCapturedProductsForSLCommunityTab(
    $input: GetCreatorProfilesCapturedProductsArgs!
  ) {
    getCreatorProfilesCapturedProducts(input: $input) {
      data {
        id
        line
        model
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

interface SocialListeningCommunityTabProps {
  community: string;
  brandId: string;
  dateRange: SimpleDateRangeFilterInput;
  selectedTab: COMMUNITY_MODAL_TYPES;
  onChange: (tab: COMMUNITY_MODAL_TYPES) => void;
}

const SocialPostSortByLabelMap = {
  [CreatorProfileCommunitySortBy.Relationship]: 'Relationship',
  [CreatorProfileCommunitySortBy.Followers]: 'Followers',
  [CreatorProfileCommunitySortBy.BrandMentions]: 'Brand Mentions',
  [CreatorProfileCommunitySortBy.BrandEngagement]: 'Brand Engagement',
  [CreatorProfileCommunitySortBy.UgcEngagementRate]: 'UGC Engagement Rate',
  [CreatorProfileCommunitySortBy.DateAddedToCommunity]:
    'Date added to your Community',
};

export const SocialListeningCommunityTab = ({
  brandId,
  community,
  dateRange,
  selectedTab,
  onChange,
}: SocialListeningCommunityTabProps) => {
  const navigate = useNavigate();
  const [selectedSort, setSelectedSort] =
    useState<CreatorProfileCommunitySortBy>(
      CreatorProfileCommunitySortBy.Relationship,
    );
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  const [getLabels] = useGetPaginatedLabelsForSlCommunityTabLazyQuery();
  const { data: locationsData } = useGetLocationsForSlCommunityTabQuery({
    variables: {
      brandId,
    },
  });

  const [getCapturedProducts] =
    useGetCapturedProductsForSlCommunityTabLazyQuery();

  const filters = useMemo(() => {
    return {
      sortBy: {
        field: selectedSort,
        order: SortOrder.Desc,
      },
      brandEngagementCount: {
        ...(filterValues.engagementGroup?.engagement?.value.max && {
          max: Number(filterValues.engagementGroup?.engagement?.value.max),
        }),
        ...(filterValues.engagementGroup?.engagement?.value.min && {
          min: Number(filterValues.engagementGroup?.engagement?.value.min),
        }),
      },
      brandMentionsCount: {
        ...(filterValues.mentionsGroup?.mentions?.value.max && {
          max: Number(filterValues.mentionsGroup?.mentions?.value.max),
        }),
        ...(filterValues.mentionsGroup?.mentions?.value.min && {
          min: Number(filterValues.mentionsGroup?.mentions?.value.min),
        }),
      },
      followersCount: {
        ...(filterValues.followersGroup?.followers?.value.max && {
          max: Number(filterValues.followersGroup?.followers?.value.max),
        }),
        ...(filterValues.followersGroup?.followers?.value.min && {
          min: Number(filterValues.followersGroup?.followers?.value.min),
        }),
      },
      relationshipStrength:
        filterValues.relationshipsGroup?.relationships?.value.map(
          (v) => v.value as CreatorProfileRelationshipStrength,
        ),
      sentiment: filterValues.sentimentsGroup?.sentiments?.value.map(
        (v) => v.value as Sentiment,
      ),
      labelIds: filterValues.labelsGroup?.labels?.value.map(
        (v) => v.value as string,
      ),
      locations: filterValues.locationsGroup?.locations?.value.map(
        (v) => v.value as string,
      ),
      capturedProductIds:
        filterValues.capturedProductsGroup?.capturedProducts?.value.map(
          (v) => v.value as string,
        ),
    };
  }, [filterValues, selectedSort]);

  return (
    <Box
      sx={{
        width: '100%',
        '& .MuiTabs-root': {
          borderBottom: 'none',
        },
        '& button.MuiTab-root': {
          pb: 0,
        },
        '& .MuiTabs-indicator': {
          display: 'none',
        },
        '& .MuiTabs-flexContainer': {
          gap: `${theme.spacing(2)} !important`,
        },
      }}
    >
      <Tabs
        className="community-tabs"
        tab={selectedTab}
        onChange={(tab) => {
          onChange(tab as COMMUNITY_MODAL_TYPES);
        }}
        tabs={[
          // {
          //   label: 'Creators',
          //   key: COMMUNITY_MODAL_TYPES.CommunityCreators,
          //   content: (
          //     <SocialListeningCommunityCreators
          //       brandId={brandId}
          //       community={community}
          //       dateRange={dateRange}
          //       {...filters}
          //     />
          //   ),
          // },
          {
            label: 'Posts',
            key: COMMUNITY_MODAL_TYPES.CommunityPosts,
            content: (
              <SocialListeningCommunityPosts
                brandId={brandId}
                community={community}
                dateRange={dateRange}
                onClickPost={(postId) => {
                  onChange(COMMUNITY_MODAL_TYPES.CommunityPost);
                  navigate(
                    `${PlotRoutes.socialListeningCommunity(
                      brandId,
                    )}?modalType=${
                      COMMUNITY_MODAL_TYPES.CommunityPost
                    }&postId=${postId}`,
                  );
                }}
                {...filters}
              />
            ),
          },
        ].map((tab) => ({
          ...tab,
          label: (
            <IconButton
              sx={{
                ...theme.typography['headline-md'],
                color:
                  tab.key === selectedTab
                    ? theme.colors?.primary.black
                    : theme.colors?.utility[700],
                borderRadius: 25,
                background:
                  tab.key === selectedTab
                    ? 'rgba(35, 6, 3, 0.07)'
                    : 'transparent',
                padding: theme.spacing(1.5, 3),
              }}
            >
              {tab.label}
            </IconButton>
          ),
        }))}
        tabsExtra={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* TODO: Add back in when BE is ready */}
            {/* {selectedTab === COMMUNITY_MODAL_TYPES.CommunityPosts && (
              <NestedFiltersMenuView
                values={{
                  sortBy: {
                    value: {
                      label: SocialPostSortByLabelMap[selectedSort],
                      value: selectedSort,
                    },
                  },
                }}
                items={[
                  {
                    type: 'single-select',
                    key: 'sortBy',
                    options: Object.values(CreatorProfileCommunitySortBy).map(
                      (value) => ({
                        label: SocialPostSortByLabelMap[value],
                        value,
                      }),
                    ),
                  },
                ]}
                onChange={(values: any) => {
                  if (values.sortBy?.value) {
                    setSelectedSort(values.sortBy.value.value);
                  }
                }}
                componentsProps={{
                  trigger: {
                    text: SocialPostSortByLabelMap[selectedSort],
                    Icon: IconLinearSort,
                    sx: {
                      ...theme.typography['subhead-xl'],
                      background: theme.colors?.primary.parchment,
                    },
                  },
                  menu: {
                    sx: {
                      background: theme.colors?.primary.parchment,
                    },
                  },
                }}
              />
            )} */}
            <NestedFiltersMenuView
              values={filterValues}
              onChange={(values) => {
                setFilterValues(values);
              }}
              componentsProps={{
                trigger: {
                  sx: {
                    background: theme.colors?.primary.parchment,
                  },
                },
                menu: {
                  sx: {
                    background: theme.colors?.primary.parchment,
                  },
                },
              }}
              // @ts-ignore
              items={[
                ...(selectedTab !== COMMUNITY_MODAL_TYPES.CommunityPosts
                  ? []
                  : [
                      {
                        type: 'group',
                        key: 'relationshipsGroup',
                        label: 'Relationship',
                        Icon: IconLinearCelo,
                        items: [
                          {
                            type: 'multi-select',
                            key: 'relationships',
                            Icon: IconLinearCelo,
                            options: [
                              CreatorProfileRelationshipStrength.VeryStrong,
                              CreatorProfileRelationshipStrength.Strong,
                              CreatorProfileRelationshipStrength.Neutral,
                              CreatorProfileRelationshipStrength.Weak,
                              CreatorProfileRelationshipStrength.VeryWeak,
                            ].map((value) => ({
                              label: RelationshipLabelMap[value],
                              value,
                            })),
                          },
                        ],
                      },
                    ]),
                {
                  type: 'group',
                  key: 'sentimentsGroup',
                  label: 'Sentiment',
                  Icon: IconCustomSmiley,
                  items: [
                    {
                      type: 'multi-select',
                      key: 'sentiments',
                      Icon: IconCustomSmiley,
                      options: [
                        Sentiment.Positive,
                        Sentiment.Neutral,
                        Sentiment.Negative,
                      ].map((value) => ({
                        label: SentimentLabelMap[value],
                        value,
                      })),
                    },
                  ],
                },
                {
                  type: 'group',
                  key: 'locationsGroup',
                  label: 'Location',
                  Icon: IconLinearLocation,
                  items: [
                    {
                      type: 'multi-select',
                      key: 'locations',
                      Icon: IconLinearLocation,
                      filterable: true,
                      options: (
                        locationsData?.getCreatorProfileLocations ?? []
                      ).map((location) => ({
                        label: location,
                        value: location,
                      })),
                    },
                  ],
                },
                {
                  type: 'group',
                  key: 'capturedProductsGroup',
                  label: 'Products',
                  Icon: IconLinearShoppingCart,
                  items: [
                    {
                      type: 'multi-select',
                      key: 'capturedProducts',
                      options: [],
                      Icon: IconLinearShoppingCart,
                      filterable: true,
                      getAsyncOptions: async ({ query, nextPageCursor }) => {
                        const capturedProductsData = await getCapturedProducts({
                          variables: {
                            input: {
                              filters: { brandId, query },
                              after: nextPageCursor,
                            },
                          },
                          fetchPolicy: 'network-only',
                        });
                        const capturedProducts =
                          capturedProductsData?.data
                            ?.getCreatorProfilesCapturedProducts.data || [];
                        const endCursor = capturedProductsData?.data
                          ?.getCreatorProfilesCapturedProducts.pageInfo
                          ?.hasNextPage
                          ? capturedProductsData?.data
                              ?.getCreatorProfilesCapturedProducts.pageInfo
                              ?.endCursor ?? ''
                          : undefined;

                        return {
                          options: capturedProducts
                            .filter(
                              (capturedProduct) =>
                                capturedProduct.model || capturedProduct.line,
                            )
                            .map((capturedProduct) => ({
                              label:
                                capturedProduct.model || capturedProduct.line,
                              value: capturedProduct.id,
                            })),
                          nextPageCursor: endCursor,
                        };
                      },
                    },
                  ],
                },
                {
                  type: 'group',
                  key: 'labelsGroup',
                  label: 'Labels',
                  Icon: IconLinearNote,
                  items: [
                    {
                      type: 'multi-select',
                      key: 'labels',
                      options: [],
                      Icon: IconLinearNote,
                      filterable: true,
                      getAsyncOptions: async ({ query, nextPageCursor }) => {
                        const labelsData = await getLabels({
                          variables: {
                            input: {
                              brandId,
                              search: query,
                              after: nextPageCursor,
                            },
                          },
                          fetchPolicy: 'network-only',
                        });
                        const labels =
                          labelsData?.data
                            ?.paginatedOrganizationCreatorProfileLabels.data ||
                          [];
                        const endCursor = labelsData?.data
                          ?.paginatedOrganizationCreatorProfileLabels.pageInfo
                          ?.hasNextPage
                          ? labelsData?.data
                              ?.paginatedOrganizationCreatorProfileLabels
                              .pageInfo?.endCursor ?? ''
                          : undefined;

                        return {
                          options: labels.map((label) => ({
                            label: label.label,
                            value: label.id,
                          })),
                          nextPageCursor: endCursor,
                        };
                      },
                    },
                  ],
                },
                ...(selectedTab === COMMUNITY_MODAL_TYPES.CommunityPosts
                  ? []
                  : [
                      {
                        type: 'group',
                        key: 'followersGroup',
                        label: 'Followers',
                        Icon: IconLinearUser,
                        items: [
                          {
                            type: 'form',
                            key: 'followers',
                            Icon: IconLinearUser,
                            fields: [
                              {
                                type: 'number',
                                key: 'min',
                                label: 'Minimum number of followers',
                              },
                              {
                                type: 'number',
                                key: 'max',
                                label: 'Maximum number of followers',
                                rules: {
                                  max: {
                                    value: 999999,
                                    message: 'Maximum value exceeded',
                                  },
                                  min: {
                                    value: 0,
                                    message: 'Minimum value is 0',
                                  },
                                },
                              },
                            ],
                          },
                        ],
                      },
                      {
                        type: 'group',
                        key: 'mentionsGroup',
                        label: 'Brand Mentions',
                        Icon: IconLinearTag2,
                        items: [
                          {
                            type: 'form',
                            key: 'mentions',
                            Icon: IconLinearTag2,
                            fields: [
                              {
                                type: 'number',
                                key: 'min',
                                label: 'Minimum number of brand mentions',
                              },
                              {
                                type: 'number',
                                key: 'max',
                                label: 'Maximum number of brand mentions',
                                rules: {
                                  max: {
                                    value: 999999,
                                    message: 'Maximum value exceeded',
                                  },
                                  min: {
                                    value: 0,
                                    message: 'Minimum value is 0',
                                  },
                                },
                              },
                            ],
                          },
                        ],
                      },
                      {
                        type: 'group',
                        key: 'engagementGroup',
                        label: 'Brand Engagement',
                        Icon: IconOutlineLike,
                        items: [
                          {
                            type: 'form',
                            key: 'engagement',
                            Icon: IconOutlineLike,
                            fields: [
                              {
                                type: 'number',
                                key: 'min',
                                label: 'Minimum number of brand engagement',
                              },
                              {
                                type: 'number',
                                key: 'max',
                                label: 'Maximum number of brand engagement',
                                rules: {
                                  max: {
                                    value: 999999,
                                    message: 'Maximum value exceeded',
                                  },
                                  min: {
                                    value: 0,
                                    message: 'Minimum value is 0',
                                  },
                                },
                              },
                            ],
                          },
                        ],
                      },
                    ]),
              ]}
            />
          </Box>
        }
        tabsExtraAlign="right"
      />
    </Box>
  );
};
